// The one shared detail modal used by every step (app.md §8). Both modes mirror
// the same layout: a sticky action bar on top (the step's actions + close), then
// the sections in one order (owner, 2026-10-08 — D-329): History, Invoice
// Summary (the PDF beside its fields), Line Items Summary, Flags (Import Control
// only), GL Summary. The AI Notes section is gone (owner, 2026-10-08 — D-330):
// the AI's notes sit under History's upload row. Every section opens and closes
// (`CollapsibleSection`); Invoice Summary, Line Items Summary and Flags start
// open (Flags since the D-329 amendment).
// View-only everywhere except Import Control and, since 2026-10-08, Denial
// Review (`editStep`, D-328), whose editable form owns its own action bar
// (Hold / Archive / Release, or Hold / Archive / Reinstate). AP/AR Approval shared the
// editor at a second gate until it merged into Import Control (owner,
// 2026-09-29 — `docs/decisions.md` D-215). The PDF pane
// shows only where the profile allows document access (`canPreviewPdf`) —
// which, since 2026-08-10, includes a company approver at any of the three
// tiers (security.md §5, owner ruling R2).
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Button,
  DialogBody,
  DialogContent,
  DialogSurface,
  Spinner,
  Caption1,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { DismissRegular } from '@fluentui/react-icons'
import type {
  ActiveHold,
  AssociatedEntity,
  Correction,
  InvoiceLineItem,
  CurrentUser,
  Decision,
  Flag,
  Hold,
  Invoice,
  InvoiceNotificationSummary,
  InvoiceUploadSummary,
  Upload,
} from '../lib/types'
import { DecisionStep } from '../lib/constants'
import {
  applyLineItemDepartmentEdit,
  applyLineItemMarketingDetailEdit,
  applyLineItemAccountEdit,
  getInvoiceBundle,
  getUpload,
  loadLineItems,
} from '../lib/data'
import { applyLinesInTurn, type ApplyLine, type BulkChoice } from '../lib/lineItemBulk'
import { errorMessage } from '../api/errors'
import { OVERLAY_BODY_STYLE, OVERLAY_SURFACE_STYLE, useOverlayGeometry } from './overlaySurface'
import { latestRequest } from '../lib/latestRequest'
import { useInvoiceTimer } from '../lib/hooks'
import { isTimedOpen } from '../lib/handlingTimer'
import { InvoiceHeaderPanel } from './InvoiceHeaderPanel'
import { ErrorBanner } from './States'
import { InvoiceLineItemsTable } from './InvoiceLineItemsTable'
import { GLSummary } from './GLSummary'
import { checkAmounts, sumKnownLineTotals, toNumber, type LineItemDetailScreen } from '../lib/format'
import { glCodeEditable } from '../lib/required'
import { PdfPane } from './PdfPane'
import { EditableInvoiceForm } from './EditableInvoiceForm'
import { InvoiceHistory } from './InvoiceHistory'
import { CollapsibleSection } from './CollapsibleSection'

const useStyles = makeStyles({
  // The surface overlays the page's content area — `overlaySurface.ts` holds
  // the geometry, shared with the Notifications dialogs since 2026-10-07.
  surface: { ...OVERLAY_SURFACE_STYLE, paddingRight: '12px' },
  body: OVERLAY_BODY_STYLE,
  // One scroll container per mode; the sticky action bar rides its top.
  // NOTHING IN IT IS WIDER THAN IT (QA 2026-09-30, C1-01, C2-02, C1-09).
  // `overflow-y: auto` computes `overflow-x` to `auto` too, so anything too
  // wide scrolls the whole body sideways, action bar included. The thing that
  // was too wide was the Invoice Summary's field grid, whose `1fr` tracks
  // could not shrink below a Fluent Input's intrinsic width; it is fixed at
  // the cause (`useSummaryGridStyles` in `lib/theme.ts`). An `overflowX:
  // 'hidden'` stood here for one release and only hid it: it cut off the
  // summary's right column, and focus could still scroll the body sideways
  // with no way back. Do not bring it back; fix whatever is too wide. The
  // line-item tables scroll inside their own wrapper (`tableScroll`).
  scroll: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  view: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // Mirrors EditableInvoiceForm's action bar: sticky, blue rule underneath.
  // Wraps, as that one does: a bar wider than the modal puts its trailing
  // buttons on a second row instead of pushing Close out of the body.
  actionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  actionTitle: { flex: 1, minWidth: 0 },
  // PDF + invoice summary side-by-side on top; line items span full width below
  // (same shape as the Import Control editor).
  topRow: {
    display: 'grid',
    // `minmax(0, 1fr)`: the two panes split the width whatever their content
    // (C1-09; the items carry `minWidth: 0` as well).
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '20px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  // THE PDF PANE FOLLOWS THE SUMMARY'S HEIGHT (owner item 3, 2026-08-17), the
  // same change and the same reason as `EditableInvoiceForm`'s own copy of this
  // rule — read that one for the full reasoning. The two layouts are deliberate
  // twins ("same shape as the Import Control editor", above), so a fix to one is
  // a fix to both or the modal reads two different ways depending on the step.
  //
  // MEASURED here too, on this component rather than inferred from its sibling:
  // the row was 440px against a 338px `InvoiceHeaderPanel`, a 68px void. (The
  // first pass at this fixed only the editor and claimed both; the owner caught
  // it on screen. Measure the component you changed, not the one beside it.)
  pdfWrap: {
    position: 'relative',
    minHeight: '340px',
    minWidth: 0,
    '@media (max-width: 900px)': { height: '300px' },
  },
  pdfFill: { position: 'absolute', inset: 0 },
  headerCol: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  editError: { color: tokens.colorPaletteRedForeground1 },
})

interface Props {
  open: boolean
  invoice: Invoice | null
  showPdf: boolean
  /** Mount the editable editor — Import Control's full surface (D-216,
      D-219), and Denial Review's since 2026-10-08, which edits every field
      Import Control does (D-328). Omit for the read-only view every other step
      uses. `editable` (below) is derived from this so the two can never
      disagree. */
  editStep?: typeof DecisionStep.ImportControl | typeof DecisionStep.DenialReview
  /** The step offers the "What is it?" half-write. Separate from
      `editDepartments` because the two are separate routes. NARROWED by
      `glCodeEditable` (`../lib/required.ts`): only Import Control, Controller
      Approval and Export Control may change either half (D-308, D-309). */
  editAccounts?: boolean
  /** The step offers the "Who is it for?" half-write, narrowed the same way. */
  editDepartments?: boolean
  /** The categories in each line's Additional Detail pop-up (owner,
      2026-09-15; D-153, D-174, D-190, D-192). Screens pass
      `categoryPickableAt(step)` from `../lib/required.ts`; the table then
      decides line by line what the server lets this step write. Its own prop rather than a reuse
      of `editDepartments` so the grant stays legible — they are separate routes
      and separate correction rows. Unset = plain text, never a locked box. */
  editAdditionalDetail?: boolean
  /** How to show GL coding in the FULLY read-only branch — no
      `editAccounts` and no `editDepartments`, so `InvoiceLineItemsTable`
      gets no edit callback either. Mirrors that table's own `codingDisplay`
      prop and is ignored the moment either of the two above is passed, exactly
      as documented there. Defaults to `'halves'`: the composed code column
      must not appear anywhere in the Invoices tab (owner item 7, 2026-08-19),
      and Archives, Closing Confirmation and Denial Review all mount this modal
      with neither edit prop set — before this default they fell
      through to `InvoiceLineItemsTable`'s own default of `'code'` and
      showed the bare composed code read-only (a gap the previous pass
      flagged rather than fixed). */
  codingDisplay?: 'code' | 'halves' | 'account-only'
  /** Which screen this modal is mounted on — passed straight through to the
      read-only branch's `InvoiceLineItemsTable`, which needs it to decide
      which empty required fields are red (`../lib/required.ts`).
      Required even for a caller that always sets `editStep`: Import
      Control's screen passes `editStep` only when the open invoice is
      decidable and falls through to this modal's read-only branch otherwise
      (its own fallback for a row the queue's filter should already have
      excluded), so `InvoiceLineItemsTable` can still be reached from a
      screen that "always" edits. */
  screen: LineItemDetailScreen
  /** WHERE THIS INVOICE IS BEING WORKED, when the viewer can open it (owner,
      2026-09-14: *"maybe we offer a button above the blue line and next to the
      'x' to 'Go To [Step]', which takes the user to that screen, where they
      can handle it with the full permissions set of that screen"*).

      A RESOLVER, not a ready-made target: the modal is mounted once and shown
      for whichever invoice is open, so the answer changes per invoice and the
      caller is the only one holding the navigation. It returns `null` when
      there is nothing to offer — a terminal invoice, or a step this viewer
      holds no role for — and the button is then not rendered rather than
      rendered dead.

      Read-only mounts only, in practice: a modal opened AT a step already has
      that step's own actions, so pointing at the screen it is already on would
      be a button that changes nothing. */
  resolveStep?: (invoice: Invoice) => { label: string; onGo: () => void } | null
  user: CurrentUser
  /** VIEW ONLY — the Analytics lens (QA 2026-10-04 B-05). The thread is shown
      without its composer: a comment is a write, the Dashboard "writes
      nothing" (docs/app.md §6), and the server refuses it under that lens. */
  viewOnly?: boolean
  onClose: () => void
  onChanged?: () => void
  /** Called after a successful release (editable mode). Falls back to onChanged. */
  onReleased?: (releasedTo?: string | null, lateEditsUnsaved?: boolean) => void
  /** Called after an archive as not-an-invoice (editable mode). Falls back to onReleased/onChanged. */
  onArchived?: () => void
  /** View-mode action buttons (Approve/Deny/etc.), rendered in the top action bar. */
  actions?: React.ReactNode
  /** Editable mode only: the open invoice's ACTIVE hold, as the screen
      knows it now (`useActiveHolds().holdFor` on the LIVE row — this modal's
      own header is read once at open and is not re-read on a hold change), and
      the screen's `HoldControl`, which the editor renders immediately left of
      Archive. A hold disables the editor's Release and Archive; its fields
      stay editable (D-282). A read-only mount puts its `HoldControl` in
      `actions` instead. */
  hold?: ActiveHold
  holdControl?: React.ReactNode
  /** Told every time the modal's line items are (re)read — so a screen's own
      Approve can name a line the queue row's counts cannot, such as one
      holding an Inactive Account with no Department yet (D-175). */
  onLineItemsChange?: (lineItems: InvoiceLineItem[]) => void
}

export function InvoiceDetailModal({
  open,
  invoice,
  showPdf,
  editStep,
  editAccounts: stepOffersAccounts,
  editDepartments: stepOffersDepartments,
  editAdditionalDetail,
  codingDisplay = 'halves',
  screen,
  resolveStep,
  user,
  viewOnly = false,
  onClose,
  onChanged,
  onReleased,
  onArchived,
  actions,
  hold,
  holdControl,
  onLineItemsChange,
}: Props) {
  const s = useStyles()
  // THE ROUTE LOCK (D-305): what the step offers, narrowed to what this
  // invoice's route still leaves open.
  const glOpen = glCodeEditable(screen)
  const editAccounts = !!stepOffersAccounts && glOpen.account
  const editDepartments = !!stepOffersDepartments && glOpen.department
  // Handling time (docs/api.md §21). Times this viewing and files it on close,
  // at a pipeline step only: a read-only lookup files nothing (code review
  // H-02, `isTimedOpen`). Renders nothing, so it cannot affect what is on screen.
  useInvoiceTimer(open && isTimedOpen(screen, viewOnly), invoice?.id, invoice?.status)
  // "Is this editable" and "which step" can never disagree — derived off the
  // one prop.
  const editable = editStep !== undefined
  const [lineItems, setLineItems] = useState<InvoiceLineItem[] | 'loading'>('loading')
  useEffect(() => {
    if (lineItems !== 'loading') onLineItemsChange?.(lineItems)
    // `onLineItemsChange` is a screen's setter; only a new read should fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems])
  const [savingLineItemId, setSavingLineItemId] = useState<string | undefined>(undefined)
  // A failed coding write's message, and the line and control it failed on, so
  // the table can tie the message to that picker (C1-09).
  const [codeError, setCodeError] = useState<{ message: string; lineItemId: string; field: string } | null>(null)
  const codeErrorId = useId()
  // Bumped on every REFUSED coding write, to remount the row's three coding
  // pickers so each re-seeds from the stored value. The re-read below already
  // restores `lineItems`, but a refusal leaves the stored value UNCHANGED, and
  // `EntityPicker` resyncs its box on that value changing — so the box kept the
  // option the reviewer chose while the banner said it was rejected and the
  // GL Summary beside it showed the real one (QA walk, 2026-08-30).
  const [pickerResetNonce, setPickerResetNonce] = useState(0)
  const rejectCoding = useCallback((message: string, lineItemId: string, field: string) => {
    setCodeError({ message, lineItemId, field })
    setPickerResetNonce((n) => n + 1)
  }, [])
  const [flags, setFlags] = useState<Flag[]>([])
  // The header as the SERVER now has it. The `invoice` prop is the queue row
  // that was clicked, and a queue row does not change while the modal is open —
  // so an invoice that arrives with no vendor and no number (the reviewer sets
  // both in this very form) kept naming itself by its uuid to a screen reader
  // until it was closed and reopened. Both reload paths already fetch the
  // header inside the bundle and threw it away; this keeps it.
  const [liveHeader, setLiveHeader] = useState<Invoice | undefined>(undefined)
  const [decisions, setDecisions] = useState<Decision[] | 'loading'>('loading')
  // History's other two reads (D-329): every hold, placed and released, and the
  // header Total's changes. Read with `decisions`, in the same bundle.
  const [holds, setHolds] = useState<Hold[]>([])
  const [totalChanges, setTotalChanges] = useState<Correction[]>([])
  // History's upload row (D-330): who uploaded it or who emailed it, the file
  // and its pages. Read once, with the open read; undefined until it lands.
  const [uploadSummary, setUploadSummary] = useState<InvoiceUploadSummary | null | undefined>(undefined)
  // The notifications sent about the upload (D-335), read with it.
  const [notifications, setNotifications] = useState<InvoiceNotificationSummary[]>([])
  const [upload, setUpload] = useState<Upload | undefined | 'loading'>('loading')
  // Bumped whenever a control OTHER than the comment composer itself has just
  // written a comment (Requires Manual Entry's reason, item 5) — passed to
  // History as `refreshNonce` so the thread re-reads without closing and
  // reopening the modal.
  const [commentNonce, setCommentNonce] = useState(0)
  // Tagged entities per line item (AE4) — what each line's Associated Entities
  // icon shows on the read-only branch (D-154). Loaded with the bundle,
  // best-effort; a failure just leaves the map empty (empty icons).
  const [entitiesByLineItem, setEntitiesByLineItem] = useState<Record<string, AssociatedEntity[]>>({})
  // Monotonic request id: only the latest load may write state, so a slow
  // response for a just-closed invoice can't overwrite the one now open.
  const loadSeq = useRef(0)
  // The post-save re-reads' own counter: two saves in a row re-read twice, and
  // only the later answer may land (`lib/latestRequest.ts`).
  const refreshes = useRef(latestRequest())
  // A FAILED READ IS SHOWN, NEVER PAINTED OVER (code review D-01, 2026-10-03).
  // `loadError`: the open read failed, so nothing of the invoice is shown — an
  // empty line list and no Flags section looked like a real invoice with no
  // lines and no flags, and the editor offered to edit it. `refreshError`: the
  // re-read after a save failed; the editor stays (its values are saved), and
  // the banner says the flags and history beside it may be stale. Both offer
  // Retry.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  // Full reload: reseeds every pane, dropping line items into 'loading' (which
  // unmounts the editable form so it reseeds on remount). For the initial open
  // and invoice switch only — never after a save (that would discard edits).
  // One round trip: the header plus whichever satellites this mode shows. The
  // read-only path also takes the per-line tagged entities (the editable form
  // owns its own entity editor and loads them per line).
  const fullReload = useCallback(async () => {
    const iid = invoice?.id
    const seq = ++loadSeq.current
    if (!iid) return
    setLineItems('loading')
    setDecisions('loading')
    setHolds([])
    setTotalChanges([])
    setUploadSummary(undefined)
    setNotifications([])
    setFlags([])
    setLiveHeader(undefined)
    setEntitiesByLineItem({})
    setLoadError(null)
    setRefreshError(null)
    try {
      const bundle = await getInvoiceBundle(
        iid,
        editable
          ? ['line_items', 'flags', 'decisions', 'holds', 'total_corrections', 'upload', 'notifications']
          : ['line_items', 'flags', 'decisions', 'holds', 'total_corrections', 'upload', 'notifications', 'associated_entities'],
      )
      if (seq !== loadSeq.current) return
      setLiveHeader(bundle.invoice)
      setLineItems(bundle.line_items ?? [])
      setFlags(bundle.flags ?? [])
      setDecisions(bundle.decisions ?? [])
      setHolds(bundle.holds ?? [])
      setTotalChanges(bundle.total_corrections ?? [])
      setUploadSummary(bundle.upload ?? null)
      setNotifications(bundle.notifications ?? [])
      const grouped: Record<string, AssociatedEntity[]> = {}
      for (const e of bundle.associated_entities ?? []) {
        const k = e.child_id ?? ''
        if (k) (grouped[k] ??= []).push(e)
      }
      setEntitiesByLineItem(grouped)
    } catch (e) {
      if (seq !== loadSeq.current) return
      // Line items stay 'loading': nothing below mounts without a read.
      setLoadError(`Couldn't load this invoice: ${errorMessage(e)}`)
    }
  }, [invoice?.id, editable])

  // Light reload: refreshes flags + decisions WITHOUT touching line items, so the
  // editable form stays mounted and its state (just persisted, or still being
  // edited) survives. Used after a save / mark-resolved.
  const lightReload = useCallback(async () => {
    const iid = invoice?.id
    if (!iid) return
    // Two checks: the invoice is still the one this read was for (`loadSeq`),
    // and no later re-read has started (`refreshes`). An older answer applied
    // last would hand the editor a stale status or hold through `liveHeader`,
    // and an older failure would raise a banner the later success had cleared.
    const seq = loadSeq.current
    const ticket = refreshes.current.begin()
    const current = () => seq === loadSeq.current && refreshes.current.isLatest(ticket)
    try {
      const bundle = await getInvoiceBundle(iid, ['flags', 'decisions', 'holds', 'total_corrections'])
      if (!current()) return
      setLiveHeader(bundle.invoice)
      setFlags(bundle.flags ?? [])
      setDecisions(bundle.decisions ?? [])
      setHolds(bundle.holds ?? [])
      setTotalChanges(bundle.total_corrections ?? [])
      setRefreshError(null)
    } catch (e) {
      if (!current()) return
      setRefreshError(`Couldn't refresh the flags and history: ${errorMessage(e)}`)
    }
  }, [invoice?.id])

  // "What is it?" — the account half-write. Same shape as the two beside
  // it, and separate from them because it is a separate route with its own
  // authority: the server re-applies the composition rule to the department on
  // an account change (D10), which is why the line items are reloaded rather
  // than patched in place.
  const handleAccountChange = useCallback(
    async (lineItem: InvoiceLineItem, accountId: string) => {
      const iid = invoice?.id
      if (!iid) return
      const seq = loadSeq.current
      setCodeError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemAccountEdit(
          iid,
          lineItem.id,
          accountId,
          lineItem.row_version,
        )
        if (!applied) {
          rejectCoding(
            reason ? `The Account change was rejected: ${reason}` : 'The Account change was rejected.',
            lineItem.id,
            'Account',
          )
          // Re-read even on a refusal. The row is unchanged on the server, but
          // the picker is showing the option the reviewer just chose, and
          // leaving it there means the screen states an account the record
          // does not have — beside a department that was never cleared, since
          // nothing was written. Snapping the table back to stored values makes
          // the banner the only thing that changed.
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
        onChanged?.()
      } catch (e) {
        setCodeError({ message: `Couldn't save the Account: ${errorMessage(e)}`, lineItemId: lineItem.id, field: 'Account' })
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged, rejectCoding],
  )
  const handleDepartmentChange = useCallback(
    async (lineItem: InvoiceLineItem, departmentId: string) => {
      const iid = invoice?.id
      if (!iid) return
      // Capture the load id BEFORE the write, not after it: fullReload bumps
      // loadSeq when the reviewer switches invoices, and a seq read after the
      // await would always match, letting this invoice's line items land in the
      // one now open (same guard shape as the open effect).
      const seq = loadSeq.current
      setCodeError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemDepartmentEdit(
          iid,
          lineItem.id,
          departmentId,
          lineItem.row_version,
        )
        if (!applied) {
          // "Department", not the old internal name: that is what this
          // control is called on every screen that shows it (owner item 8,
          // 2026-08-19, `docs/decisions.md` D-43). Missed by the rename, like
          // the GL Summary pill beside it (QA walk, 2026-08-30).
          rejectCoding(
            reason ? `The Department change was rejected: ${reason}` : 'The Department change was rejected.',
            lineItem.id,
            'Department',
          )
          // Re-read on a refusal, same reason as the two handlers above.
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
        onChanged?.()
      } catch (e) {
        setCodeError({ message: `Couldn't save the Department: ${errorMessage(e)}`, lineItemId: lineItem.id, field: 'Department' })
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged, rejectCoding],
  )
  // The Marketing Category (owner, 2026-09-15 and 2026-09-28) — same shape as
  // the two half-writes above, including the `loadSeq` capture BEFORE the write
  // and the re-read on a refusal. The version sent is the DETAIL ROW's own,
  // never the line item's, and `0` is what says "believed not to exist yet" —
  // which is the common case here, since the row is created by the first pick.
  const handleCategoryChange = useCallback(
    async (lineItem: InvoiceLineItem, categoryId: string) => {
      const label = 'Marketing Category'
      const iid = invoice?.id
      if (!iid) return
      const seq = loadSeq.current
      setCodeError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemMarketingDetailEdit(
          iid,
          lineItem.id,
          categoryId,
          lineItem.marketing_detail?.row_version ?? 0,
        )
        if (!applied) {
          rejectCoding(reason ? `The ${label} change was rejected: ${reason}` : `The ${label} change was rejected.`, lineItem.id, label)
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh)
        onChanged?.()
      } catch (e) {
        setCodeError({ message: `Couldn't save the ${label}: ${errorMessage(e)}`, lineItemId: lineItem.id, field: label })
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged, rejectCoding],
  )
  // THE LINE-ITEM BULK BAR'S APPLY (owner, 2026-09-29 — D-228). No route
  // writes one Account onto several lines, so each selected line gets the
  // writes its own pickers make — `PATCH …/account`, then `PATCH …/department`
  // when the plan names one — in line order, one at a time
  // (`applyLinesInTurn`). A Department-only Apply (D-305) makes the department
  // write alone. Each records its own correction, as a one-by-one
  // edit does. The first refusal stops the run; the lines always reload, so
  // what did land shows, and the message says which lines did and did not.
  const handleBulkApply = useCallback(
    async (selection: InvoiceLineItem[], choice: BulkChoice) => {
      const iid = invoice?.id
      if (!iid) return
      const seq = loadSeq.current
      setCodeError(null)
      const asLine = (c: InvoiceLineItem): ApplyLine => ({
        id: c.id,
        line_number: c.line_number,
        row_version: c.row_version,
        account_id: c.account_id,
        department_id: c.department_id,
        total: c.total,
      })
      const ordered = [...selection].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0))
      const result = await applyLinesInTurn(ordered.map(asLine), choice, {
        account: async (line, accountId) => {
          const r = await applyLineItemAccountEdit(iid, line.id, accountId, line.row_version)
          return { applied: r.applied, reason: r.reason, line: r.lineItem ? asLine(r.lineItem) : undefined }
        },
        department: async (line, departmentId) => {
          const r = await applyLineItemDepartmentEdit(iid, line.id, departmentId, line.row_version)
          return { applied: r.applied, reason: r.reason, line: r.lineItem ? asLine(r.lineItem) : undefined }
        },
      })
      const fresh = await loadLineItems(iid)
      if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
      onChanged?.()
      // Thrown, not returned: the bar owns this message, so it renders beside
      // the control the reviewer just used.
      if (!result.ok) throw new Error(result.message)
    },
    [invoice?.id, onChanged],
  )

  // Measure the overlay geometry as the modal opens and lock the page scroll
  // while it is up (`overlaySurface.ts`).
  useOverlayGeometry(open)

  // THE OPEN READ: the bundle and the PDF's upload record, under ONE load id.
  // The open effect and the failed-read Retry both call this. Retry used to
  // call `fullReload` alone, which moved the load id on, so a PDF answer still
  // in flight was dropped and a failed one never asked again: the pane stayed
  // on its spinner (code review, 2026-10-03).
  const uploadId = invoice?.upload_id
  const openRead = useCallback(() => {
    void fullReload() // bumps loadSeq synchronously; the upload fetch shares that id
    const seq = loadSeq.current
    if (showPdf && uploadId) {
      setUpload('loading')
      getUpload(uploadId)
        .then((u) => { if (seq === loadSeq.current) setUpload(u) })
        .catch(() => { if (seq === loadSeq.current) setUpload(undefined) })
    } else {
      setUpload(undefined)
    }
  }, [fullReload, showPdf, uploadId])

  useEffect(() => {
    if (!open || !invoice) return
    openRead()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id])

  if (!invoice) return null

  // Resolved once per render from the OPEN invoice — the modal is mounted once
  // and shown for whichever row was clicked, so this cannot be hoisted above
  // the null guard.
  const stepTarget = resolveStep?.(invoice) ?? null

  // THE HEADER AS THE DETAIL READ RETURNED IT, falling back to the clicked row
  // only until that read lands. A list row may omit fields the detail read
  // carries — the mock's queue and archive views strip the AI notes and the
  // printed amounts — so everything below that shows a header value reads this.
  const shown = liveHeader ?? invoice

  // HISTORY, FIRST (owner, 2026-10-08 — D-329): Decision History and Comments
  // in one list, with the Total's changes. ONE mount, in both branches — the
  // editable form takes it as a slot, under its action bar. The Analytics lens
  // gets no comment box: a comment is a write (QA 2026-10-04 B-05). Its first
  // row is the upload, with the AI's notes under it (D-330), read from the
  // header the detail read returned — a queue row may omit the notes.
  const history = (
    <InvoiceHistory
      parentId={invoice.id}
      invoice={shown}
      upload={uploadSummary}
      notifications={notifications}
      decisions={decisions}
      holds={holds}
      totalChanges={totalChanges}
      user={viewOnly ? undefined : user}
      refreshNonce={commentNonce}
      divider={false}
    />
  )

  // THE AMOUNTS' BORDER ON THE READ-ONLY STEPS (owner, 2026-09-25 — D-148;
  // yellow, not red, since D-248): Tax, Shipping, Subtotal and Total go yellow
  // together whenever the stored amounts do not reconcile (within the
  // three-cent tolerance, D-151) — the same arithmetic the Line Items Summary
  // footer's red pill shows, and the same four fields the editable form marks.
  // Not on a blank Total — that is a missing figure, not a sum that fails —
  // matching the editable form's `amountsDoNotReconcile`.
  const amountsDoNotReconcile =
    lineItems !== 'loading' &&
    lineItems.length > 0 &&
    shown.total != null &&
    String(shown.total).trim() !== '' &&
    !checkAmounts(
      sumKnownLineTotals(lineItems.map((c) => c.total)),
      shown.tax,
      shown.total,
      shown.shipping,
    ).reconciles

  const pdfPane = (
    <PdfPane
      upload={upload}
      pageStart={invoice.upload_page_start ?? undefined}
      pageEnd={invoice.upload_page_end ?? undefined}
      // Lets the pane offer "Full Screen" (item 2, 2026-08-13): a new tab at
      // `/?doc=<id>`, which `main.tsx` recognizes before mounting the app
      // shell and feeds the same invoice + page span into the same PdfPane —
      // one restriction, enforced in one place, never a second signed URL
      // handed out of this component.
      invoiceId={invoice.id}
    />
  )

  // The read failed: say so, offer Retry and Close, and show nothing of the
  // invoice (D-01). Same shape in both modes.
  const failedBody = loadError && (
    <div className={mergeClasses(s.scroll, s.view)}>
      <div className={s.actionBar}>
        <div className={s.actionTitle} />
        <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />
      </div>
      <ErrorBanner message={loadError} onRetry={openRead} />
    </div>
  )

  const body = failedBody ? failedBody : editable ? (
    lineItems === 'loading' || !liveHeader ? (
      // Mount the form only once the read has landed — it seeds its editable
      // state once on mount, from the header and lines that read returned.
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner label="Loading line items…" />
      </div>
    ) : (
      <div className={s.scroll}>
        <ErrorBanner message={refreshError} onRetry={() => void lightReload()} />
        <EditableInvoiceForm
          key={invoice.id}
          // The header as the read just returned it, not the queue row that
          // was clicked: a row up to 30 seconds old seeded a stale header and
          // row version, and the first save came back 409 (code review D-11).
          invoice={liveHeader}
          step={editStep}
          lineItems={lineItems}
          flags={flags}
          pdf={showPdf ? pdfPane : null}
          history={history}
          onClose={onClose}
          hold={hold}
          holdControl={holdControl}
          onChanged={() => {
            lightReload()
            // The editor posts the Requires Manual Entry reason to the thread
            // and then calls this, so the thread must refetch; a save with no
            // new comment just re-reads the same list (item 5, 2026-08-12).
            setCommentNonce((n) => n + 1)
            onChanged?.()
          }}
          onReleased={(releasedTo, lateEditsUnsaved) => {
            if (onReleased) onReleased(releasedTo, lateEditsUnsaved)
            else onChanged?.()
            onClose()
          }}
          onArchived={() => {
            ;(onArchived ?? onReleased ?? onChanged)?.()
            onClose()
          }}
        />
      </div>
    )
  ) : (
    // Keyed by invoice, so each invoice opens with the sections at their
    // defaults rather than as the last one was left (D-329).
    <div key={invoice.id} className={mergeClasses(s.scroll, s.view)}>
      <div className={s.actionBar}>
        {/* Spacer, not a title (item 8, C2, 2026-08-13): the vendor/external-id
            line was dropped as redundant with the Invoice Summary panel below.
            `s.actionTitle` is `flex: 1, minWidth: 0` — the only thing pushing
            `actions` + Close to the right — so an empty div in its place keeps
            that layout without restoring the text. The Dialog carries no
            DialogTitle (see below), so the removed Subtitle2 was never this
            dialog's accessible name and none needs replacing. */}
        <div className={s.actionTitle} />
        {actions}
        {/* "Go To <Step>" (owner, 2026-09-14) — beside the X, above the blue
            rule, on a read-only mount that can say where the invoice actually
            is. `resolveStep` answers null when there is nothing to offer (a
            step this viewer holds no role for, or whose queue would not list
            this invoice for them — D-252, `goToNodeFor`), so this
            is absent rather than disabled: a dead button next to Close reads
            as a control that failed, not as one that does not apply. */}
        {stepTarget && (
          <Button appearance="secondary" onClick={stepTarget.onGo}>
            {stepTarget.label}
          </Button>
        )}
        <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />
      </div>
      {history}
      {/* The heading in the left-hand corner, above the PDF and the fields,
          like every other section's (owner, 2026-10-08 — D-329). */}
      <CollapsibleSection title="Invoice Summary" defaultOpen>
        <div className={s.topRow} style={showPdf ? undefined : { display: 'block' }}>
          {showPdf && (
            <div className={s.pdfWrap}>
              <div className={s.pdfFill}>{pdfPane}</div>
            </div>
          )}
          <div className={s.headerCol}>
            <InvoiceHeaderPanel invoice={shown} flags={flags} amountsDoNotReconcile={amountsDoNotReconcile} />
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Line Items Summary" defaultOpen>
        {/* A notification, so a refused coding write is announced; the picker it was
            refused on names it as its description (QA 2026-10-04, C1-09). */}
        {codeError && (
          <Caption1 id={codeErrorId} role="alert" className={s.editError}>
            {codeError.message}
          </Caption1>
        )}
        {/* `boxedFields`: every locked field in this modal wears one treatment
            (owner item 2, 2026-08-28). Passed explicitly rather than left to the
            table's own default, which would leave the fully read-only steps —
            Closing Confirmation, Denial Review, Archives — showing bare text
            under a boxed `InvoiceHeaderPanel`. */}
        <InvoiceLineItemsTable
          lineItems={lineItems}
          screen={screen}
          subtotal={shown.subtotal}
          tax={shown.tax}
          shipping={shown.shipping}
          total={shown.total}
          invoice={invoice}
          entitiesByLineItem={entitiesByLineItem}
          boxedFields
          onAccountChange={editAccounts ? handleAccountChange : undefined}
          onDepartmentChange={editDepartments ? handleDepartmentChange : undefined}
          onCategoryChange={editAdditionalDetail ? handleCategoryChange : undefined}
          // Wherever a line's Account or Department may change on this invoice
          // (D-228, D-305), held or not (D-282). The table applies the Department
          // alone where the Account is locked.
          onBulkApply={editAccounts || editDepartments ? handleBulkApply : undefined}
          savingLineItemId={savingLineItemId}
          pickerResetNonce={pickerResetNonce}
          refusal={codeError ? { lineItemId: codeError.lineItemId, field: codeError.field, messageId: codeErrorId } : undefined}
          codingDisplay={codingDisplay}
        />
      </CollapsibleSection>
      {lineItems !== 'loading' && (
        <CollapsibleSection title="GL Summary">
          <GLSummary
            subtotal={shown.subtotal}
            tax={shown.tax}
            shipping={shown.shipping}
            total={shown.total}
            rows={lineItems.map((c) => ({
              codeId: c.final_code_id,
              codeName: c.code_name,
              accountId: c.account_id,
              total: toNumber(c.total),
              // The tie-break key the distributed view needs: it spreads the
              // charges across these LINES exactly as the CSV does — remainder
              // cents and the vendor's variance on the largest line, ties to the
              // LOWEST line number — then adds them up by code (D-151).
              lineNumber: c.line_number,
            }))}
          />
        </CollapsibleSection>
      )}
    </div>
  )

  // Don't mount the focus-trapping Dialog until the editable form's data is
  // ready. If the form mounts LATE (cold open, line items still loading), Fluent's
  // Dialog auto-focus re-fires when the form appears and yanks focus to the
  // first action-bar button — stealing it from the field the reviewer just
  // clicked (the cold-first-open focus-steal; confirmed via the focus log:
  // input → button.fui-Button ~100ms after the click). Opening only once the
  // content is present makes Fluent's initial focus run exactly once. View mode
  // is unaffected (contentReady is always true there; its panes self-spinner).
  const contentReady = !editable || loadError !== null || (lineItems !== 'loading' && liveHeader !== undefined)

  return (
    <>
      {open && editable && !contentReady && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner label="Loading invoice…" />
        </div>
      )}
      <AppDialog open={open && contentReady} onOpenChange={(_, d) => { if (!d.open) onClose() }}>
      <DialogSurface
        className={s.surface}
        aria-label={`${liveHeader?.vendor_name ?? invoice.vendor_name ?? 'Invoice'} ${
          liveHeader?.external_invoice_id ?? invoice.external_invoice_id ?? invoice.id
        }`}
      >
        <DialogBody className={s.body}>
          {/* Both modes carry their own sticky action bar (actions + close),
              so there is no DialogTitle row. The read-only mode's bar no
              longer carries the vendor/number line either (owner item 8,
              2026-08-13), which left the dialog with no accessible name at
              all — hence the `aria-label` above. It is the only thing now
              naming the record to a screen reader, so keep it in step with
              whatever the header shows. */}
          <DialogContent>{body}</DialogContent>
        </DialogBody>
      </DialogSurface>
      </AppDialog>
    </>
  )
}
